const BASE = process.env.REACT_APP_API_URL || '';

export function mediaUrl(path) {
  if (!path) return path;
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  if (!BASE) return path;
  return `${BASE}${path.startsWith('/') ? '' : '/'}${path}`;
}

function getAuthToken(path) {
  const staffToken = sessionStorage.getItem('auth-token');
  if (path && path.startsWith('/api/admin')) {
    return staffToken;
  }
  if (path && path.startsWith('/api/users')) {
    return localStorage.getItem('user-token') || staffToken;
  }
  return staffToken || localStorage.getItem('user-token');
}

async function request(path, options = {}) {
  const token = getAuthToken(path);
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { headers, credentials: 'include', ...options });
  if (!res.ok) {
    if (res.status === 502 || res.status === 503) {
      throw new Error('Server is restarting. Wait a minute and try again.');
    }
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

export const api = {
  setToken: (token) => {
    if (token) sessionStorage.setItem('auth-token', token);
    else sessionStorage.removeItem('auth-token');
  },
  getAdminAuthMode: async () => {
    const res = await fetch(`${BASE}/api/admin/auth-mode`, {
      credentials: 'include',
    });
    if (!res.ok) {
      if (res.status === 502 || res.status === 503) {
        throw new Error('Server is restarting. Wait a minute and try again.');
      }
      const err = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Request failed');
    }
    return res.json();
  },
  adminLogin: async (email, password) => {
    const res = await fetch(`${BASE}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      if (res.status === 502 || res.status === 503) {
        throw new Error('Server is restarting. Wait a minute and try again.');
      }
      const err = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Invalid email or password');
    }
    const result = await res.json();
    if (result.token) sessionStorage.setItem('auth-token', result.token);
    return result;
  },
  adminLogout: () => request('/api/admin/logout', { method: 'POST' }),
  getAdminMe: () => request('/api/admin/me'),
  getAdminAuditLog: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const q = qs.toString();
    return request(`/api/admin/audit-log${q ? `?${q}` : ''}`);
  },
  getAdminAccounts: () => request('/api/admin/admin-users'),
  createAdminAccount: (data) =>
    request('/api/admin/admin-users', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminAccount: (id, data) =>
    request(`/api/admin/admin-users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteAdminAccount: (id) =>
    request(`/api/admin/admin-users/${id}`, { method: 'DELETE' }),
  changeAdminPassword: (currentPassword, newPassword) =>
    request('/api/admin/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  verifyPin: async (pin, type) => {
    const res = await fetch(`${BASE}/api/admin/verify-pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ pin, type }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Invalid PIN');
    }
    const result = await res.json();
    if (result.token) {
      sessionStorage.setItem('auth-token', result.token);
    }
    return result;
  },
  getMenu: (venue) => {
    const qs = venue ? `?venue=${venue}` : '';
    return request(`/api/menu/full${qs}`);
  },
  getMenuStatus: () => request('/api/menu/status'),
  getVenues: () => request('/api/menu/venues'),
  reserveStock: (session_id, items) =>
    request('/api/menu/reserve', {
      method: 'POST',
      body: JSON.stringify({ session_id, items }),
    }),
  releaseStock: (session_id, items) =>
    request('/api/menu/release', {
      method: 'POST',
      body: JSON.stringify({ session_id, items }),
    }),
  touchStockReservations: (session_id) =>
    request('/api/menu/reserve/touch', {
      method: 'POST',
      body: JSON.stringify({ session_id }),
    }),
  placeOrder: (order) =>
    request('/api/orders', {
      method: 'POST',
      body: JSON.stringify(order),
    }),
  getOrder: (orderNumber) => request(`/api/orders/${orderNumber}`),
  getOrderGroup: (groupId) => request(`/api/orders/group/${groupId}`),
  getOrders: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/orders${qs ? `?${qs}` : ''}`);
  },
  updateOrderStatus: (id, status) =>
    request(`/api/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  updateOrderItemsStatus: (id, status, category) =>
    request(`/api/orders/${id}/items-status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, category }),
    }),
  reprintCafeStickers: (id, { category } = {}) =>
    request(`/api/orders/${id}/reprint-stickers`, {
      method: 'POST',
      body: JSON.stringify(category ? { category } : {}),
    }),
  previewOpenCafeStickers: (slice) =>
    request('/api/orders/cafe-stickers/print', {
      method: 'POST',
      body: JSON.stringify({ slice, dry_run: true }),
    }),
  printOpenCafeStickers: (slice) =>
    request('/api/orders/cafe-stickers/print', {
      method: 'POST',
      body: JSON.stringify({ slice }),
    }),
  clearOpenCafeOrders: (pin) =>
    request('/api/orders/cafe/clear-open', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    }),
  printKitchenDocket: ({ orderId, orderNumber, clientPrints = false, stationId } = {}) =>
    request(orderId ? `/api/orders/${orderId}/print-docket` : '/api/orders/print-docket', {
      method: 'POST',
      body: JSON.stringify({
        order_id: orderId || undefined,
        order_number: orderNumber || undefined,
        client_prints: clientPrints || undefined,
        station_id: stationId || undefined,
      }),
    }),
  getSettings: () => request('/api/admin/settings'),
  getStuckPayments: () => request('/api/admin/stuck-payments'),
  getAdminUsers: () => request('/api/admin/users'),
  exportAdminUsersCsv: async () => {
    const token = sessionStorage.getItem('auth-token');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${BASE}/api/admin/users/export.csv`, {
      headers,
      credentials: 'include',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Export failed' }));
      throw new Error(err.error || 'Export failed');
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/i);
    const filename = match?.[1] || `st-barb-accounts-${new Date().toISOString().slice(0, 10)}.csv`;
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
    return { filename };
  },
  getAdminUserOrders: (userId) => request(`/api/admin/users/${userId}/orders`),
  deleteAdminUser: (userId) =>
    request(`/api/admin/users/${userId}`, { method: 'DELETE' }),
  verifyAdminUser: (userId) =>
    request(`/api/admin/users/${userId}/verify-email`, { method: 'POST' }),
  setAdminUserSundaySchool: (userId, status) =>
    request(`/api/admin/users/${userId}/sunday-school`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  updateSettings: (settings) =>
    request('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  notifyOrdersOpen: (payload = {}) =>
    request('/api/admin/notify-orders-open', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getAdminCategories: () => request('/api/admin/categories'),
  createCategory: (data) =>
    request('/api/admin/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateCategory: (id, data) =>
    request(`/api/admin/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteCategory: (id) =>
    request(`/api/admin/categories/${id}`, { method: 'DELETE' }),
  reorderCategories: (categories) =>
    request('/api/admin/categories/reorder', {
      method: 'PUT',
      body: JSON.stringify({ categories }),
    }),
  getAdminItems: () => request('/api/admin/items'),
  createItem: (data) =>
    request('/api/admin/items', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateItem: (id, data) =>
    request(`/api/admin/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  updateItemStock: (id, stock_quantity) =>
    request(`/api/admin/items/${id}/stock`, {
      method: 'PATCH',
      body: JSON.stringify({ stock_quantity }),
    }),
  deleteItem: (id) =>
    request(`/api/admin/items/${id}`, { method: 'DELETE' }),
  reorderItems: (items) =>
    request('/api/admin/items/reorder', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    }),
  getStats: () => request('/api/admin/stats'),
  getTodayOrders: () => request('/api/admin/today-orders'),
  getAdminDashboard: ({ sundays = 12, sunday = null } = {}) => {
    const params = new URLSearchParams();
    if (sundays) params.set('sundays', String(sundays));
    if (sunday) params.set('sunday', sunday);
    const qs = params.toString();
    return request(`/api/admin/dashboard${qs ? `?${qs}` : ''}`);
  },
  deactivateMenuItems: (scope) =>
    request('/api/admin/items/deactivate-bulk', {
      method: 'POST',
      body: JSON.stringify({ scope }),
    }),
  getTemplates: () => request('/api/admin/templates'),
  saveTemplate: (name) =>
    request('/api/admin/templates', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  loadTemplate: (id) =>
    request(`/api/admin/templates/${id}/load`, { method: 'POST' }),
  deleteTemplate: (id) =>
    request(`/api/admin/templates/${id}`, { method: 'DELETE' }),
  uploadItemImage: async (itemId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    const headers = {};
    const token = getAuthToken('/api/admin');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${BASE}/api/upload/item-image/${itemId}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || 'Upload failed');
    }
    return res.json();
  },
  deleteItemImage: (itemId) =>
    request(`/api/upload/item-image/${itemId}`, { method: 'DELETE' }),
  resetDaily: () =>
    request('/api/admin/reset-daily', { method: 'POST' }),
  clearStuckPayments: () =>
    request('/api/admin/clear-stuck-payments', { method: 'POST' }),
  clearIOSBadges: () =>
    request('/api/admin/clear-ios-badges', { method: 'POST' }),
  getSalesReport: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/admin/sales-report${qs ? `?${qs}` : ''}`);
  },
  getSchedule: () => request('/api/admin/schedule'),
  updateSchedule: (schedule) =>
    request('/api/admin/schedule', {
      method: 'PUT',
      body: JSON.stringify(schedule),
    }),
  getVapidPublicKey: () => request('/api/push/vapid-public'),
  subscribePush: (subscription, orderIds) =>
    request('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription, orderIds }),
    }),
  registerDevice: (token, orderIds, platform) =>
    request('/api/push/register-device', {
      method: 'POST',
      body: JSON.stringify({ token, orderIds, platform }),
    }),
  confirmPayment: (orderIds, paySecret, payRequestId) =>
    request('/api/payments/confirm', {
      method: 'POST',
      body: JSON.stringify({ order_ids: orderIds, pay_secret: paySecret, pay_request_id: payRequestId }),
    }),
  createPayRequest: (orderIds, { save_card } = {}) =>
    request('/api/payments/create-pay-request', {
      method: 'POST',
      body: JSON.stringify({
        order_ids: orderIds,
        ...(save_card ? { save_card: true } : {}),
      }),
    }),
  getTyroConfig: () => request('/api/payments/tyro/config'),
  getPayMethods: () => request('/api/payments/pay-methods'),

  deletePayMethod: (payMethodId) =>
    request(`/api/payments/pay-methods/${encodeURIComponent(payMethodId)}`, {
      method: 'DELETE',
    }),
  getFastingMode: () => request('/api/admin/fasting'),
  toggleFasting: (fasting) =>
    request('/api/admin/fasting-toggle', {
      method: 'POST',
      body: JSON.stringify({ fasting }),
    }),

  // Category Order Windows
  getCategoryWindows: () => request('/api/admin/category-windows'),
  updateCategoryWindows: (windows) =>
    request('/api/admin/category-windows', {
      method: 'PUT',
      body: JSON.stringify(windows),
    }),
  getPublicCategoryWindows: () => request('/api/menu/category-windows'),

  // Modifier Groups
  getModifierGroups: () => request('/api/admin/modifier-groups'),
  createModifierGroup: (data) =>
    request('/api/admin/modifier-groups', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateModifierGroup: (id, data) =>
    request(`/api/admin/modifier-groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteModifierGroup: (id) =>
    request(`/api/admin/modifier-groups/${id}`, { method: 'DELETE' }),

  // Modifier Options
  addModifierOption: (groupId, data) =>
    request(`/api/admin/modifier-groups/${groupId}/options`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateModifierOption: (id, data) =>
    request(`/api/admin/modifier-options/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteModifierOption: (id) =>
    request(`/api/admin/modifier-options/${id}`, { method: 'DELETE' }),

  // Item Modifier Assignments
  getItemModifiers: (itemId) =>
    request(`/api/admin/items/${itemId}/modifiers`),
  updateItemModifiers: (itemId, groups) =>
    request(`/api/admin/items/${itemId}/modifiers`, {
      method: 'PUT',
      body: JSON.stringify({ groups }),
    }),

  // User Auth
  register: (name, email, password, { sundaySchoolRequest = false } = {}) =>
    request('/api/users/register', {
      method: 'POST',
      body: JSON.stringify({
        name,
        email,
        password,
        sunday_school_request: Boolean(sundaySchoolRequest),
      }),
    }),
  login: async (email, password) => {
    const res = await fetch(`${BASE}/api/users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({ error: 'Request failed' }));
    if (!res.ok) {
      const err = new Error(data.error || 'Request failed');
      if (data.needsVerification) {
        err.needsVerification = true;
        err.email = data.email;
      }
      throw err;
    }
    return data;
  },
  forgotPassword: async (email) => {
    const res = await fetch(`${BASE}/api/users/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  },
  resetPassword: async (token, password) => {
    const res = await fetch(`${BASE}/api/users/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  },
  verifyEmail: async (token) => {
    const res = await fetch(`${BASE}/api/users/verify-email?token=${encodeURIComponent(token)}`, { credentials: 'include' });
    const data = await res.json().catch(() => ({ error: 'Request failed' }));
    if (!res.ok) throw new Error(data.error || 'Verification failed');
    return data;
  },
  resendVerification: async (email) => {
    const res = await fetch(`${BASE}/api/users/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({ error: 'Request failed' }));
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  },
  getMe: () => request('/api/users/me'),
  logout: () => request('/api/users/logout', { method: 'POST' }),
  updateMe: (data) =>
    request('/api/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  getMyOrders: () => request('/api/users/orders'),
  deleteMyAccount: () =>
    request('/api/users/me', { method: 'DELETE' }),

  getRoseCafeFood: () => request('/api/rose'),
  getKitchenSales: (kitchen) => request(`/api/kitchen-sales/${kitchen}`),

  // --- Counter POS ---
  // Uses the same /api/orders endpoint the customer app uses (shared stock,
  // shared kitchen split, shared order-number generation). The counter
  // token attached to the request unlocks the `cash` / `card_paid` /
  // `split` payment methods.
  createCounterOrder: (payload) =>
    request('/api/orders', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getEftposStatus: (payRequestId) =>
    request(`/api/pos/eftpos/status?pay_request_id=${encodeURIComponent(payRequestId)}`),
  cancelEftpos: (payRequestId) =>
    request('/api/pos/eftpos/cancel', {
      method: 'POST',
      body: JSON.stringify({ pay_request_id: payRequestId }),
    }),
  answerEftposPrompt: (payRequestId, decisionId, answer) =>
    request('/api/pos/eftpos/answer-prompt', {
      method: 'POST',
      body: JSON.stringify({
        pay_request_id: payRequestId,
        decision_id: decisionId,
        answer,
      }),
    }),
  lookupPosRefund: (orderNumber) =>
    request(`/api/pos/refunds/lookup?order_number=${encodeURIComponent(orderNumber)}`),
  startPosRefund: (payload, stationKey) => {
    const qs = stationKey ? `?station=${encodeURIComponent(stationKey)}` : '';
    return request(`/api/pos/refunds${qs}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  printEftposReceipt: (payload, stationKey) => {
    const qs = stationKey ? `?station=${encodeURIComponent(stationKey)}` : '';
    return request(`/api/pos/eftpos/print-receipt${qs}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  // Public list of active stations for the POS lock-screen picker. Only
  // returns id / name / station_key — TIDs and printer IPs stay behind
  // the counter/admin auth wall.
  listPosStations: (deviceId) => {
    const qs = deviceId ? `?device=${encodeURIComponent(deviceId)}` : '';
    return request(`/api/pos/stations${qs}`);
  },
  claimPosStation: (stationKey, deviceId) =>
    request('/api/pos/stations/claim', {
      method: 'POST',
      body: JSON.stringify({ station_key: stationKey, device_id: deviceId }),
    }),
  releasePosStation: (stationKey, deviceId) =>
    request('/api/pos/stations/release', {
      method: 'POST',
      body: JSON.stringify({ station_key: stationKey, device_id: deviceId }),
    }),
  heartbeatPosStation: (stationKey, deviceId) =>
    request('/api/pos/stations/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ station_key: stationKey, device_id: deviceId }),
    }),
  adminReleasePosStation: (id) =>
    request(`/api/admin/pos-stations/${id}/release`, { method: 'POST' }),
  getPosMenuManage: () => request('/api/pos/menu-manage'),
  setPosItemActive: (id, active) =>
    request(`/api/pos/items/${id}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ active: active ? 1 : 0 }),
    }),
  bumpPosItemStock: (id, delta) =>
    request(`/api/pos/items/${id}/stock`, {
      method: 'PATCH',
      body: JSON.stringify({ delta }),
    }),
  bumpPosOptionStock: (id, delta) =>
    request(`/api/pos/modifier-options/${id}/stock`, {
      method: 'PATCH',
      body: JSON.stringify({ delta }),
    }),
  getPosTodayOrders: () => request('/api/pos/today-orders'),
  getPosModifierGroups: () => request('/api/pos/modifier-groups'),
  addPosModifierOption: (groupId, { name, surcharge }) =>
    request(`/api/pos/modifier-groups/${groupId}/options`, {
      method: 'POST',
      body: JSON.stringify({ name, surcharge }),
    }),
  deletePosModifierOption: (id) =>
    request(`/api/pos/modifier-options/${id}`, { method: 'DELETE' }),
  getPosConfig: (stationKey) => {
    const qs = stationKey ? `?station=${encodeURIComponent(stationKey)}` : '';
    return request(`/api/pos/config${qs}`);
  },
  updatePosConfig: (config, stationKey) => {
    const qs = stationKey ? `?station=${encodeURIComponent(stationKey)}` : '';
    return request(`/api/pos/config${qs}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },
  // Admin-side CRUD for pos_stations. Each row is one physical iPad +
  // EFTPOS terminal pair at the counter.
  getPosStations: () => request('/api/admin/pos-stations'),
  createPosStation: (data) =>
    request('/api/admin/pos-stations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updatePosStation: (id, data) =>
    request(`/api/admin/pos-stations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deletePosStation: (id) =>
    request(`/api/admin/pos-stations/${id}`, { method: 'DELETE' }),
  printDockets: (orderIds) =>
    request('/api/pos/print-dockets', {
      method: 'POST',
      body: JSON.stringify(
        Array.isArray(orderIds) ? { order_ids: orderIds } : { order_id: orderIds }
      ),
    }),
  printOutstandingKitchenDockets: ({ kitchen, clientPrints = false, stationId } = {}) =>
    request('/api/orders/print-outstanding-kitchen', {
      method: 'POST',
      body: JSON.stringify({
        kitchen: kitchen || undefined,
        client_prints: clientPrints || undefined,
        station_id: stationId || undefined,
      }),
    }),
  reprintOrder: (orderId) =>
    request('/api/pos/reprint', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId }),
    }),
  testPrinter: (ip, port, station, { clientPrints = false } = {}) =>
    request('/api/pos/test-printer', {
      method: 'POST',
      body: JSON.stringify({ ip, port, station, client_prints: clientPrints || undefined }),
    }),
  // station: 'cafe' (default cold-drinks/cafe-food) or 'cafe_hot' (espresso-side)
  testCafePrinter: (station = 'cafe') =>
    request('/api/pos/test-cafe-printer', {
      method: 'POST',
      body: JSON.stringify({ station }),
    }),
  getPosCafeStickerStatus: (orderIds, waitedMs = 0) => {
    const ids = (Array.isArray(orderIds) ? orderIds : [orderIds])
      .map((n) => Number(n))
      .filter((n) => Number.isInteger(n) && n > 0);
    const qs = new URLSearchParams();
    qs.set('order_ids', ids.join(','));
    if (waitedMs) qs.set('waited_ms', String(Math.max(0, Math.floor(waitedMs))));
    return request(`/api/pos/cafe-stickers?${qs.toString()}`);
  },
  getPosCafePrinters: () => request('/api/pos/cafe-printers'),
  reprintCafeOrderStickers: (id) =>
    request(`/api/orders/${id}/reprint-stickers`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  reprintPosCafeStickers: (orderIds) =>
    request('/api/pos/reprint-cafe-stickers', {
      method: 'POST',
      body: JSON.stringify({
        order_ids: (Array.isArray(orderIds) ? orderIds : [orderIds])
          .map((n) => Number(n))
          .filter((n) => Number.isInteger(n) && n > 0),
      }),
    }),
  getPrintAgentStatus: () => request('/api/admin/print-agent'),
  rotatePrintAgentToken: () =>
    request('/api/admin/print-agent/rotate-token', { method: 'POST' }),
  queueCafeTestSticker: (station = 'cafe') => {
    const qs = station && station !== 'cafe' ? `?station=${encodeURIComponent(station)}` : '';
    return request(`/api/admin/print-agent/test-sticker${qs}`, { method: 'POST' });
  },
  cleanupStalePrintJobs: ({ olderThanHours = 24, dryRun = false, type = 'cafe_sticker' } = {}) =>
    request('/api/admin/print-agent/cleanup-stale', {
      method: 'POST',
      body: JSON.stringify({
        older_than_hours: olderThanHours,
        dry_run: dryRun,
        type,
      }),
    }),
  requeueFailedPrintJobs: ({ olderThanHours = null, dryRun = false, type = 'cafe_sticker' } = {}) =>
    request('/api/admin/print-agent/requeue-failed', {
      method: 'POST',
      body: JSON.stringify({
        ...(olderThanHours != null ? { older_than_hours: olderThanHours } : {}),
        dry_run: dryRun,
        type,
      }),
    }),
};
